import { API, apiUrl } from '@/config';
import { ERRORS } from '@/constants';
import type { ApiErrorBody, FieldErrors } from '@/types';
import { clearTokens, getAccessToken, markSessionLikely, setAccessToken } from './tokens';

/**
 * The only place this portal talks to the network.
 *
 * Everything goes through here so that authentication, token refresh, error
 * shaping and timeouts are handled once rather than at each call site.
 */

/**
 * A failed request, with the server's own error code preserved.
 *
 * Components branch on `code`, never on the message: codes are a stable
 * contract, messages are copy that will be reworded and eventually translated.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string;
  readonly fieldErrors: FieldErrors | undefined;

  constructor(status: number, body: ApiErrorBody | null, fallback: string) {
    super(body?.error.message ?? fallback);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.error.code ?? 'unknown';
    this.requestId = body?.error.requestId ?? '';
    this.fieldErrors = isFieldErrors(body?.error.details) ? body.error.details : undefined;
  }

  /** Retrying will not help; the caller should sign in again. */
  get isAuthFailure(): boolean {
    return this.status === 401;
  }
}

function isFieldErrors(value: unknown): value is FieldErrors {
  if (typeof value !== 'object' || value === null) return false;
  return Object.values(value).every((v) => Array.isArray(v));
}

/**
 * Called when the session is gone for good.
 *
 * A callback rather than an import of the store, so this module stays free of
 * any dependency on Redux - which is what lets it be tested on its own and
 * copied into a portal that manages state differently.
 */
type SessionEndedHandler = () => void;
let onSessionEnded: SessionEndedHandler = () => undefined;

export function setSessionEndedHandler(handler: SessionEndedHandler): void {
  onSessionEnded = handler;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Skips the Authorization header. Login and refresh set this. */
  anonymous?: boolean;
  /**
   * Returns the whole envelope rather than unwrapping `data`.
   *
   * Only paginated endpoints need it: they carry `meta` beside `data`, and the
   * total in there is what tells a page whether there is another one. Every
   * other call is better off with the payload directly.
   */
  envelope?: boolean;
}

/**
 * Guards against a refresh stampede.
 *
 * Several queries commonly fail with 401 at the same moment - a dashboard
 * firing four requests when the token has just expired. Without this they would
 * each try to refresh with the same cookie, and the server cannot tell that
 * from a stolen token being replayed: the second exchange of one refresh token
 * is exactly what reuse detection acts on. It would revoke the whole session
 * family and sign the user out with a security warning, over nothing.
 *
 * So this is not a nicety any more. Sharing one in-flight promise means one
 * refresh and four retries, which is the only shape the server accepts.
 */
let refreshInFlight: Promise<boolean> | null = null;

/**
 * Waits between refresh attempts the server could not answer. Short, because
 * somebody is looking at a spinner or a failed request while this runs; three
 * tries cover a dev server restarting or a laptop's wifi coming back.
 */
const REFRESH_RETRY_DELAYS_MS = [1_000, 3_000];

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * One refresh at a time across every tab of this portal, not just this one.
 *
 * `refreshInFlight` below stops one tab racing itself; it cannot see a second
 * tab. Two tabs whose tokens lapse together would both present the same
 * cookie, the server would see the second as a replayed token, and revoke the
 * whole session in both. Under the lock the second tab waits, and by the time
 * it runs the first has stored the rotated cookie, so it presents that one.
 *
 * Web Locks are per origin, so each portal has its own. Where the API is
 * missing (old browsers, tests) this degrades to the per-tab guard alone.
 */
function withRefreshLock<T>(run: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  return locks ? locks.request('nudent-refresh', run) : run();
}

/**
 * Exchanges the refresh cookie for a new access token.
 *
 * Resolves `true` on success and `false` when the server REFUSED - the
 * session is revoked, expired, or was never there. Only that second case
 * signs anybody out.
 *
 * Anything else - offline, the server restarting, a 5xx, a 429 - is not an
 * answer about the session at all, and it used to be treated as one: every
 * blip cleared the tokens AND the session hint, so the next reload did not
 * even try the perfectly good cookie and showed the login form. Now those are
 * retried, and if they still fail they throw, which reaches the caller as an
 * ordinary failed request while the session stays intact for next time.
 */
async function refreshSession(): Promise<boolean> {
  for (let attempt = 0; ; attempt += 1) {
    /*
     * No body and nothing to look up: the refresh token is in an httpOnly
     * cookie, so the browser attaches it and this code never sees it. The
     * server replies with a new access token and a replacement cookie.
     *
     * `credentials: 'include'` is what makes both halves of that work. Without
     * it a cross-origin request neither sends the cookie nor honours the
     * Set-Cookie coming back - and the portal and the API are different origins
     * in every environment, including localhost with two ports.
     */
    const response = await fetch(apiUrl(API.endpoints.refresh), {
      method: 'POST',
      credentials: 'include',
    }).catch(() => null);

    if (response?.ok) {
      const payload = (await response.json()) as { data: { accessToken: string } };
      setAccessToken(payload.data.accessToken);
      markSessionLikely();
      return true;
    }

    // The server looked at the cookie and said no. That is final.
    if (response && (response.status === 401 || response.status === 403)) {
      clearTokens();
      return false;
    }

    const delay = REFRESH_RETRY_DELAYS_MS[attempt];
    if (delay === undefined) {
      throw response
        ? await parseError(response, ERRORS.generic)
        : new ApiError(0, null, ERRORS.network);
    }
    await sleep(delay);
  }
}

export function refreshOnce(): Promise<boolean> {
  refreshInFlight ??= withRefreshLock(refreshSession).finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function parseError(response: Response, fallback: string): Promise<ApiError> {
  const body = (await response.json().catch(() => null)) as ApiErrorBody | null;
  return new ApiError(response.status, body, fallback);
}

/**
 * One extra chance, not an infinite loop.
 *
 * A single failed refresh-and-retry pair was treated as a dead session, and
 * that turned out to be too strict: caught live, a request retried right
 * after a successful refresh could still come back 401 - and a completely
 * fresh call moments later (what clicking "Try again" does) succeeded every
 * time. That is a race, not a revoked session, and the fix is to give the
 * automatic path the same second chance the manual button already had,
 * rather than making a person supply it by hand. Bounded, so a session that
 * really is gone does not retry forever.
 */
const MAX_REFRESH_ATTEMPTS = 2;

async function send<T>(path: string, options: RequestOptions, attempt = 0): Promise<T> {
  const { method = 'GET', body, signal, anonymous = false, envelope = false } = options;

  /*
   * A request that hangs forever looks identical to a broken app. The timeout
   * is combined with any caller-supplied signal so a component unmounting still
   * cancels the request.
   */
  const timeout = AbortSignal.timeout(API.timeoutMs);
  const combined = signal ? AbortSignal.any([signal, timeout]) : timeout;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';

  const token = getAccessToken();
  if (!anonymous && token) headers.authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      method,
      headers,
      /*
       * Sent on every request, not only the auth ones.
       *
       * Login needs it to RECEIVE the Set-Cookie, logout needs it to receive
       * the clear, and a cross-origin response's cookies are ignored without
       * it. Blanket rather than selective because the cookie is path-scoped to
       * /auth server-side, so this cannot widen where it actually travels -
       * and a per-call opt-in is the kind of thing somebody forgets on the one
       * endpoint that needed it.
       */
      credentials: 'include',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      signal: combined,
    });
  } catch (cause) {
    // fetch only rejects for network-level failures; HTTP errors resolve.
    if (signal?.aborted) throw cause;
    throw new ApiError(0, null, ERRORS.network);
  }

  if (response.status === 401 && !anonymous) {
    if (attempt < MAX_REFRESH_ATTEMPTS) {
      // Throws, rather than resolving false, when the server could not be
      // reached - so an outage fails this request without ending the session.
      const refreshed = await refreshOnce();
      if (refreshed) return send<T>(path, options, attempt + 1);
    }

    // Either the refresh itself was refused, or every retry attempt still
    // came back 401. Either way this is not a fluke any more: end the
    // session for real so the UI reflects it - leaving Redux saying "signed
    // in" beside a request that will never succeed is worse than a login
    // screen, because nothing on the page tells anyone why it is broken.
    clearTokens();
    onSessionEnded();
    throw await parseError(response, ERRORS.unauthorized);
  }

  if (!response.ok) throw await parseError(response, ERRORS.generic);

  if (response.status === 204) return undefined as T;

  /*
   * Every success is enveloped as { data }, so unwrapping happens here and
   * callers work with the payload directly - except where the caller asked for
   * the envelope, which is how a paginated response keeps its `meta`.
   */
  const payload = (await response.json()) as { data: T };
  return envelope ? (payload as unknown as T) : payload.data;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    send<T>(path, { ...options, method: 'GET' }),
  /** GET, keeping `{ data, meta }` intact. For paginated endpoints. */
  getEnveloped: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    send<T>(path, { ...options, method: 'GET', envelope: true }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    send<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    send<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    send<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    send<T>(path, { ...options, method: 'DELETE' }),
};
